import React, { useMemo } from "react";
import useTranslation from "../../i18n/useTranslation";

export default function DivisionSummary({ colors, teamsCount, totalGames, activeSeasonData }) {
    const { t } = useTranslation();

    const styles = useMemo(() => {
        const cardBase = {
            borderRadius: 16,
            border: "1px solid rgba(0,0,0,0.08)",
            background: "#fff",
            boxShadow: "0 10px 24px rgba(0,0,0,0.06)",
            padding: 14,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            minHeight: 88,
        };

        const iconWrap = {
            width: 46,
            height: 46,
            borderRadius: 14,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: colors.secondary,
            border: "1px solid rgba(0,0,0,0.10)",
            color: colors.textOnSecondary,
            boxShadow: "0 10px 18px rgba(0,0,0,0.10)",
            flex: "0 0 auto",
        };

        const label = {
            fontSize: 12.5,
            fontWeight: 900,
            letterSpacing: "0.02em",
            color: "rgba(20,21,26,0.60)",
            textTransform: "uppercase",
            marginBottom: 6,
            lineHeight: 1.1,
        };

        const value = {
            fontSize: 22,
            fontWeight: 900,
            color: "rgba(20,21,26,0.92)",
            lineHeight: 1,
        };

        const rightHint = {
            fontSize: 12.5,
            fontWeight: 700,
            color: "rgba(20,21,26,0.50)",
            lineHeight: 1.15,
            textAlign: "right",
            maxWidth: 140,
        };

        return {
            cardBase,
            iconWrap,
            label,
            value,
            rightHint,
            body: { display: "flex", flexDirection: "column" },
            left: { display: "flex", alignItems: "center", gap: 12, minWidth: 0 },
            metricText: { minWidth: 0 },
        };
    }, [colors.secondary, colors.textOnSecondary]);

    const stagesCount = activeSeasonData?.stages?.length || 0;
    const playersCount = activeSeasonData?.playersCount || 0;
    const eventsCount = totalGames || 0;
    const teams = teamsCount || 0;

    const MetricCard = ({ icon, title, value, hint }) => (
        <div className="col-md-6 mb-3">
            <div className="kbola-metric-card" style={styles.cardBase}>
                <div style={styles.left}>
                    <div style={styles.iconWrap}>
                        <i className={`fas ${icon}`} style={{ fontSize: 18 }} />
                    </div>

                    <div style={styles.metricText}>
                        <div style={styles.label}>{title}</div>
                        <div style={styles.value}>{value}</div>
                    </div>
                </div>

                {hint ? <div style={styles.rightHint}>{hint}</div> : null}
            </div>
        </div>
    );

    return (
        <>
            <div className="row mt-3 mb-3">
                <MetricCard
                    icon="fa-layer-group"
                    title={t("divisionSummary.stages")}
                    value={stagesCount}
                    hint={t("divisionSummary.competitionPhases")}
                />

                <MetricCard
                    icon="fa-flag-checkered"
                    title={t("divisionSummary.teams")}
                    value={teams}
                    hint={t("divisionSummary.registeredTeams")}
                />

                <MetricCard
                    icon="fa-calendar-alt"
                    title={t("divisionSummary.events")}
                    value={eventsCount}
                    hint={t("divisionSummary.scheduledGames")}
                />

                <MetricCard
                    icon="fa-users"
                    title={t("divisionSummary.players")}
                    value={playersCount}
                    hint={t("divisionSummary.activeRosterSize")}
                />
            </div>

            <style>{`
        .kbola-metric-card {
          transition: transform 140ms ease, box-shadow 140ms ease;
        }
        .kbola-metric-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 14px 30px rgba(0,0,0,0.10);
        }
      `}</style>
        </>
    );
}